import {useEffect, useRef, useState} from 'react';
import {getPageConfig} from '../../config/pageConfigs';
import {getOrderedPersonas, usePersonas} from '../../config/personas';
import {ACCOUNT_ID} from '../../api/account';
import {createVideoGeneration, generateText as apiGenerateText, getGenerationStatus} from '../../api/video';
import {generateAudioPreview} from '../../api/audio';
import {trackEvent} from '../../utils/analytics';
import {showError} from '../../utils/notify';

// Star Card Component
function StarCard({star, isSelected, onSelect, isMuted, onToggleMute}) {
    const videoRef = useRef(null);
    const [isVideoLoaded, setIsVideoLoaded] = useState(false);

    // Play/pause video based on selection
    useEffect(() => {
        if (videoRef.current) {
            if (isSelected) {
                videoRef.current.play().catch(() => {
                });
            } else {
                videoRef.current.pause();
                videoRef.current.currentTime = 0;
                setIsVideoLoaded(false);
            }
        }
    }, [isSelected]);

    // Update muted state
    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.muted = isMuted;
        }
    }, [isMuted]);

    return (
        <div
            onClick={() => onSelect(star)}
            className="flex-shrink-0 cursor-pointer w-[140px]"
        >
            <div
                className={`rounded-[20px] bg-white w-full relative transition-all p-[10px] ${isSelected ? 'ring-2 ring-brand-red ring-offset-0' : ''}`}>
                <div className="h-[180px] rounded-[10px] overflow-hidden relative">
                    {/* Always show image as background */}
                    <img
                        src={star.image}
                        alt={star.name}
                        className={`w-full h-full object-cover object-top absolute inset-0 transition-opacity duration-300 ${isSelected && isVideoLoaded ? 'opacity-0' : 'opacity-100'}`}
                    />
                    {/* Video overlay when selected */}
                    {isSelected && star.video && (
                        <>
                            <video
                                ref={videoRef}
                                src={star.video}
                                className={`w-full h-full object-cover object-top absolute inset-0 transition-opacity duration-300 ${isVideoLoaded ? 'opacity-100' : 'opacity-0'}`}
                                loop
                                muted={isMuted}
                                playsInline
                                onCanPlay={() => setIsVideoLoaded(true)}
                            />
                            {/* Loading spinner while video loads */}
                            {!isVideoLoaded && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                                    <div
                                        className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                </div>
                            )}
                            {/* Mute/Unmute button */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onToggleMute?.();
                                }}
                                className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center hover:bg-black/70 transition z-10"
                            >
                                {isMuted ? (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white"
                                         strokeWidth="2">
                                        <path d="M11 5L6 9H2v6h4l5 4V5z"/>
                                        <line x1="23" y1="9" x2="17" y2="15"/>
                                        <line x1="17" y1="9" x2="23" y2="15"/>
                                    </svg>
                                ) : (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white"
                                         strokeWidth="2">
                                        <path d="M11 5L6 9H2v6h4l5 4V5z"/>
                                        <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
                                    </svg>
                                )}
                            </button>
                        </>
                    )}
                </div>
                <div className="pt-[10px] flex items-end justify-between">
                    <p className="text-sm font-medium text-brand-dark leading-[1.3] tracking-[-0.14px] w-[85px]">
                        {star.name}
                    </p>
                    {isSelected && (
                        <div
                            className="w-6 h-6 rounded-full bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange flex items-center justify-center">
                            <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                                <path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round"
                                      strokeLinejoin="round"/>
                            </svg>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Кружок плеера в плашке превью: серый — озвучки для текущего текста нет,
// градиентный — превью готово; иконка меняется на паузу во время игры.
function PreviewPlayCircle({active = false, playing = false, loading = false}) {
    return (
        <span
            className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                active
                    ? 'bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white'
                    : 'bg-brand-dark/10 text-brand-dark/40'
            }`}
        >
            {loading ? (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10"
                            stroke="currentColor" strokeWidth="4" fill="none"/>
                    <path className="opacity-75" fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
            ) : playing ? (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                    <rect x="2" y="2" width="3.5" height="10" rx="1"/>
                    <rect x="8.5" y="2" width="3.5" height="10" rx="1"/>
                </svg>
            ) : (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                    <path d="M4 2.5l8 4.5-8 4.5v-9z"/>
                </svg>
            )}
        </span>
    );
}

// Scroll Arrows
function ScrollArrows({onPrev, onNext}) {
    return (
        <div className="flex gap-[5px]">
            <button
                onClick={onPrev}
                className="w-[40px] h-[28px] rounded-lg bg-white flex items-center justify-center hover:bg-gray-50 transition"
            >
                <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
                    <path d="M7 1L1 7L7 13" stroke="#221d2d" strokeWidth="2" strokeLinecap="round"
                          strokeLinejoin="round"/>
                </svg>
            </button>
            <button
                onClick={onNext}
                className="w-[40px] h-[28px] rounded-lg bg-white flex items-center justify-center hover:bg-gray-50 transition"
            >
                <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
                    <path d="M1 1L7 7L1 13" stroke="#221d2d" strokeWidth="2" strokeLinecap="round"
                          strokeLinejoin="round"/>
                </svg>
            </button>
        </div>
    );
}


// Тексты храним по звёздам ({celebId: text}) — как в звонках: смена звезды
// очищает поле, возврат восстанавливает. Старый строковый ключ мигрируем
// в запись текущей звезды.
const GENERATED_TEXTS_KEY = 'celebs_generated_texts';
const LEGACY_TEXT_KEY = 'celebs_generated_text';
// Готовое превью переживает перезагрузку (файл уже на S3) — не гоняем TTS
// заново; эта же озвучка уйдёт в видео.
const PREVIEW_KEY = 'celebs_preview';

// Лимит текста видео (у звонков 500): промпт ИИ просит до 250, а
// generate-audio-preview на бэкенде принимает до 280.
const MAX_TEXT_LENGTH = 250;

// Main Interface Card
export default function MainCard({
                                     credits,
                                     selectedPersonaId,
                                     onPersonaChange,
                                     onGenerationStart,
                                     onGenerationComplete,
                                     onAccountStatusUpdate,
                                     onNoCredits,
                                     isExampleVideoPlaying = false,
                                     onStopExampleVideo
                                 }) {
    const pageConfig = getPageConfig();

    // Live catalog from the admin (static fallback until it loads)
    const personas = usePersonas();

    // Get ordered personas (preselected first)
    const orderedPersonas = getOrderedPersonas(pageConfig.preSelectedPersonaId, personas);

    // Form state - derive selectedPersona from controlled prop
    const selectedPersona = personas.find(p => p.id === selectedPersonaId) || orderedPersonas[0];

    const [generatedTexts, setGeneratedTexts] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(GENERATED_TEXTS_KEY) || 'null');
            if (saved && typeof saved === 'object' && !Array.isArray(saved)) {
                return Object.fromEntries(
                    Object.entries(saved).filter(([, text]) => typeof text === 'string')
                );
            }
        } catch {
            // повреждённое значение — просто начинаем без текстов
        }
        // Миграция со старого одиночного ключа — текст достаётся текущей звезде.
        const legacy = (localStorage.getItem(LEGACY_TEXT_KEY) || '').slice(0, MAX_TEXT_LENGTH);
        return legacy.trim() ? {[selectedPersona.id]: legacy} : {};
    });
    const generatedText = generatedTexts[selectedPersona.id] || '';
    // Пишет текст под звезду, выбранную на момент вызова: если человек
    // переключил звезду, пока шла генерация, результат не попадёт к новой.
    const setGeneratedText = (text) => {
        setGeneratedTexts(prev => ({...prev, [selectedPersona.id]: text}));
    };

    // Текст правится только в явном режиме (Править → Сохранить/Отмена):
    // до сохранения показанный текст не меняется и превью остаётся
    // актуальным, черновик живёт отдельно.
    const [isEditingText, setIsEditingText] = useState(false);
    const [draftText, setDraftText] = useState('');

    // Одно свободное поле: кого поздравляем, повод, детали — всё сразу. Из
    // него одной кнопкой генерим и текст, и аудиопревью.
    const [aiDetails, setAiDetails] = useState(
        pageConfig.preSelectedRecipient ? `Поздравление ${pageConfig.preSelectedRecipient}` : ''
    );
    const [isGenerating, setIsGenerating] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Video mute state for selected persona
    const [isVideoMuted, setIsVideoMuted] = useState(true);

    // Mute main video when example video starts playing
    useEffect(() => {
        if (isExampleVideoPlaying) {
            setIsVideoMuted(true);
        }
    }, [isExampleVideoPlaying]);

    // Аудиопревью обязательно перед генерацией: именно это аудио станет
    // озвучкой видео (audio_url с S3 уходит в /generate или на покупку).
    // {key, audioUrl, durationSec, blobUrl}; после перезагрузки blobUrl
    // нет — играем прямо с S3 (audioUrl публичный).
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewPlaying, setPreviewPlaying] = useState(false);
    const [previewError, setPreviewError] = useState(null);
    const [preview, setPreview] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(PREVIEW_KEY) || 'null');
            if (saved && typeof saved.key === 'string' && typeof saved.audioUrl === 'string') {
                return {
                    key: saved.key,
                    audioUrl: saved.audioUrl,
                    durationSec: saved.durationSec ?? null,
                    blobUrl: null,
                };
            }
        } catch {
            // повреждённое значение — просто начинаем без превью
        }
        return null;
    });
    const previewAudioRef = useRef(null);
    const previewBlobUrlRef = useRef(null);

    const starsRef = useRef(null);

    // Превью привязано к паре «звезда + текст»: смена любого из них требует
    // послушать заново, без этого генерация видео закрыта.
    const currentPreviewKey = `${selectedPersona.id}::${generatedText.trim()}`;
    const previewReady = preview != null && preview.key === currentPreviewKey;

    // Save generated texts to localStorage when they change
    useEffect(() => {
        localStorage.setItem(GENERATED_TEXTS_KEY, JSON.stringify(generatedTexts));
    }, [generatedTexts]);

    useEffect(() => {
        // blobUrl не сериализуем — после перезагрузки хватит audioUrl с S3.
        if (preview) {
            localStorage.setItem(PREVIEW_KEY, JSON.stringify({
                key: preview.key,
                audioUrl: preview.audioUrl,
                durationSec: preview.durationSec,
            }));
        }
    }, [preview]);

    const stopPreview = () => {
        previewAudioRef.current?.pause();
        previewAudioRef.current = null;
        setPreviewPlaying(false);
    };

    // Drop the preview audio on unmount
    useEffect(() => () => {
        previewAudioRef.current?.pause();
        if (previewBlobUrlRef.current) URL.revokeObjectURL(previewBlobUrlRef.current);
    }, []);

    const playPreviewUrl = (url) => {
        stopPreview();
        const audio = new Audio(url);
        previewAudioRef.current = audio;
        setPreviewPlaying(true);
        audio.onended = () => setPreviewPlaying(false);
        audio.play().catch(() => setPreviewPlaying(false));
    };

    // TTS-превью для готового текста + автовоспроизведение. Общий шаг для
    // основного пути («Составить текст» из деталей) и переозвучки из редактора.
    const generatePreviewForText = async (text) => {
        stopPreview();
        trackEvent('generate_video_preview');
        setPreviewLoading(true);
        setPreviewError(null);
        try {
            const data = await generateAudioPreview({celebId: selectedPersona.id, wishText: text});
            const audioBlob = new Blob(
                [Uint8Array.from(atob(data.audio_base64), c => c.charCodeAt(0))],
                {type: 'audio/mpeg'}
            );
            if (previewBlobUrlRef.current) URL.revokeObjectURL(previewBlobUrlRef.current);
            previewBlobUrlRef.current = URL.createObjectURL(audioBlob);
            setPreview({
                key: `${selectedPersona.id}::${text}`,
                audioUrl: data.audio_url,
                durationSec: data.duration_sec ?? null,
                blobUrl: previewBlobUrlRef.current,
            });
            playPreviewUrl(previewBlobUrlRef.current);
        } catch (error) {
            console.error('Preview error:', error);
            if (error.status === 429) {
                setPreviewError('Слишком много превью подряд. Подождите минуту.');
            } else {
                setPreviewError('Не получилось сделать превью. Попробуйте ещё раз.');
            }
        } finally {
            setPreviewLoading(false);
        }
    };

    // Основной путь: детали → ИИ-текст → аудиопревью, всё одной кнопкой.
    const handleGenerate = async () => {
        const details = aiDetails.trim();
        if (!details) {
            showError('Расскажите, кого и с чем поздравляем');
            return;
        }

        trackEvent('generate_text');
        setIsGenerating(true);
        try {
            // Поле ввода одно (как в звонках), а у видео-эндпоинта их два —
            // всё описание уезжает темой, details оставляем пустым.
            const data = await apiGenerateText({
                celebId: selectedPersona.id,
                concept: details,
                details: '',
            });
            // Бэкенд сам перезапрашивает текст, пока тот не влезет в лимит;
            // срез — страховка на случай ответа от старой версии API.
            const text = (data.text || '').slice(0, MAX_TEXT_LENGTH).trim();
            setGeneratedText(text);
            setIsEditingText(false);
            await generatePreviewForText(text);
        } catch (error) {
            console.error('AI generation error:', error);
            showError('Ошибка генерации текста. Попробуйте ещё раз.');
        } finally {
            setIsGenerating(false);
        }
    };

    // Переозвучка сохранённого текста: без ИИ, читаем то, что показано.
    const handleRegenerateFromText = async () => {
        const text = generatedText.trim();
        if (!text) {
            showError('Напишите текст поздравления');
            return;
        }
        await generatePreviewForText(text);
    };

    // Смена звезды: текст очищается/восстанавливается сам (generatedTexts по
    // celebId), а звук и незаконченную правку останавливаем — черновик
    // принадлежит прошлой звезде.
    const handlePersonaSelect = (persona) => {
        if (persona.id === selectedPersona.id) return;
        stopPreview();
        setIsEditingText(false);
        setPreviewError(null);
        onPersonaChange?.(persona.id);
    };

    const startEditing = () => {
        stopPreview();
        setDraftText(generatedText);
        setIsEditingText(true);
    };

    const cancelEditing = () => setIsEditingText(false);

    // Сохранение правки сразу озвучивает новый текст — плашка это и обещает
    // («Сохраните — и озвучим заново»). Неизменённый текст не переозвучиваем.
    const saveEditedText = async () => {
        const text = draftText.trim();
        if (!text) {
            showError('Напишите текст поздравления');
            return;
        }
        setGeneratedText(text);
        setIsEditingText(false);
        if (preview?.key !== `${selectedPersona.id}::${text}`) {
            await generatePreviewForText(text);
        }
    };

    const handleTogglePlay = () => {
        if (previewPlaying) {
            stopPreview();
            return;
        }
        // Восстановленное из localStorage превью играет прямо с S3.
        if (previewReady) playPreviewUrl(preview.blobUrl || preview.audioUrl);
    };

    // Poll job status
    const pollJobStatus = async (jobId) => {
        while (true) {
            try {
                const data = await getGenerationStatus(jobId);
                if (data.status === 'completed') {
                    onGenerationComplete?.(data.video_url);
                    onAccountStatusUpdate?.();
                    return;
                } else if (data.status === 'failed') {
                    showError(`Ошибка: ${data.error || 'Не удалось сгенерить видео'}`);
                    onGenerationComplete?.(null);
                    onAccountStatusUpdate?.();
                    return;
                }
                await new Promise(resolve => setTimeout(resolve, 5000));
            } catch (error) {
                console.error('Polling error:', error);
                showError('Ошибка проверки статуса. Попробуйте ещё раз.');
                onGenerationComplete?.(null);
                return;
            }
        }
    };

    // Handle form submission
    const handleSubmit = async () => {
        const text = generatedText.trim();
        if (!text || !previewReady) {
            showError('Сначала сгенерируйте превью — этим голосом будет озвучено видео');
            return;
        }

        // Прослушанное превью — оно и станет озвучкой видео. Без кредитов
        // заявка уезжает в пейвол: после оплаты вебхук сам запустит генерацию.
        const audio = {url: preview.audioUrl, durationSec: preview.durationSec};

        // Check if user has credits
        if (credits <= 0) {
            onNoCredits?.(selectedPersona, text, audio);
            return;
        }

        setIsSubmitting(true);
        try {
            const jobData = await createVideoGeneration({
                celebId: selectedPersona.id,
                wishText: text,
                accountId: ACCOUNT_ID,
                audioUrl: audio.url,
            });
            const jobId = jobData.id;

            // Update account status (credits and history)
            onAccountStatusUpdate?.();

            // Start generation animation and poll for status
            onGenerationStart?.(selectedPersona);
            pollJobStatus(jobId);
        } catch (error) {
            if (error.data?.error === 'insufficient_credits') {
                onNoCredits?.(selectedPersona, text, audio);
                return;
            }
            showError('Ошибка создания заказа. Попробуйте ещё раз.');
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const scrollStars = (direction) => {
        if (starsRef.current) {
            const scrollAmount = direction === 'next' ? 300 : -300;
            starsRef.current.scrollBy({left: scrollAmount, behavior: 'smooth'});
        }
    };

    return (
        <div className="max-w-[1110px] mx-auto px-4">
            <div className="bg-brand-gray rounded-[20px] md:rounded-[30px] p-0 relative">
                <div className="flex flex-col lg:flex-row">
                    {/* Left side - Selectors */}
                    <div className="flex-1 min-w-0 p-4 pb-4 lg:p-[30px]">
                        {/* Star Selector */}
                        <div className="mb-6">
                            <div className="flex items-center justify-between mb-4 pr-4 lg:pr-0">
                                <h3 className="text-xl md:text-2xl font-bold text-brand-dark leading-[1.1] tracking-[-0.24px]">
                                    Выберите звезду
                                </h3>
                                <ScrollArrows
                                    onPrev={() => scrollStars('prev')}
                                    onNext={() => scrollStars('next')}
                                />
                            </div>
                            <div className="relative overflow-hidden">
                                <div
                                    ref={starsRef}
                                    className="flex gap-[5px] overflow-x-scroll no-scrollbar py-1 pl-1"
                                    style={{scrollBehavior: 'smooth'}}
                                >
                                    {orderedPersonas.map(star => (
                                        <StarCard
                                            key={star.id}
                                            star={star}
                                            isSelected={selectedPersona?.id === star.id}
                                            onSelect={handlePersonaSelect}
                                            isMuted={isVideoMuted}
                                            onToggleMute={() => {
                                                if (isVideoMuted && isExampleVideoPlaying) {
                                                    // User is unmuting while example is playing - stop example
                                                    onStopExampleVideo?.();
                                                }
                                                setIsVideoMuted(!isVideoMuted);
                                            }}
                                        />
                                    ))}
                                    {/* Spacer for fade effect */}
                                    <div className="flex-shrink-0 w-[100px]"/>
                                </div>
                                <div
                                    className="absolute right-0 top-0 bottom-0 w-[120px] gradient-fade-right pointer-events-none"/>
                            </div>
                        </div>
                    </div>

                    {/* Right side - text and submit */}
                    <div className="w-full lg:w-[420px] lg:flex-shrink-0">
                        <div
                            className="bg-white rounded-[20px] m-2 p-4 lg:m-[10px] lg:p-[20px] h-auto lg:h-[calc(100%-20px)] flex flex-col">

                            {/* Details input: one free-form field for the AI text */}
                            <div className="border border-dashed border-[#d3d2d5] rounded-[16px] p-3.5 mb-4">
                                <p className="text-sm font-medium text-brand-dark mb-2">Кого и с чем поздравляем</p>
                                <textarea
                                    value={aiDetails}
                                    onChange={(e) => setAiDetails(e.target.value)}
                                    placeholder="Кого поздравляем и по какому поводу: имя, детали..."
                                    rows={2}
                                    className="w-full px-4 py-3 rounded-[10px] border border-[#d3d2d5] text-base font-medium placeholder:text-brand-dark/50 focus:outline-none focus:border-brand-red transition bg-white resize-none"
                                />
                                <button
                                    onClick={handleGenerate}
                                    disabled={!aiDetails.trim() || isGenerating || previewLoading}
                                    className="w-full mt-2 py-3 rounded-xl bg-brand-dark text-white text-sm font-medium tracking-[-0.14px] hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {isGenerating ? (
                                        <>
                                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10"
                                                        stroke="currentColor" strokeWidth="4" fill="none"/>
                                                <path className="opacity-75" fill="currentColor"
                                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                            </svg>
                                            Генерируем…
                                        </>
                                    ) : generatedText.trim() ? 'Составить текст заново' : 'Составить текст'}
                                </button>
                            </div>

                            {/* Текст и превью — одна карточка: сверху текст, снизу
                                приклеенная плашка озвучки. Текст меняется только через
                                Сохранить, до этого превью остаётся актуальным. */}
                            <div className="flex-1 mb-3">
                                {(generatedText.trim() || isEditingText) && (
                                    <div className="rounded-[16px] border border-[#eceaee] overflow-hidden">
                                        {isEditingText ? (
                                            <>
                                                <textarea
                                                    value={draftText}
                                                    onChange={(e) => setDraftText(e.target.value)}
                                                    placeholder="Напишите текст поздравления..."
                                                    maxLength={MAX_TEXT_LENGTH}
                                                    autoFocus
                                                    aria-label="Текст поздравления"
                                                    className="w-full h-[150px] px-4 pt-3 text-base font-medium text-brand-dark placeholder:text-brand-dark/50 focus:outline-none resize-none"
                                                />
                                                <div className="flex items-center gap-2 px-3 pb-3">
                                                    <button
                                                        onClick={saveEditedText}
                                                        disabled={previewLoading}
                                                        className="px-5 py-2.5 rounded-xl bg-brand-dark text-white text-sm font-medium hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Сохранить
                                                    </button>
                                                    <button
                                                        onClick={cancelEditing}
                                                        className="px-5 py-2.5 rounded-xl border border-[#d3d2d5] bg-white text-sm font-medium text-brand-dark hover:bg-gray-50 transition"
                                                    >
                                                        Отмена
                                                    </button>
                                                    <span className="ml-auto text-sm text-brand-dark/50 tracking-[-0.14px]">
                                                        {draftText.length}/{MAX_TEXT_LENGTH}
                                                    </span>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <p className="px-4 pt-3 pb-2 text-base font-medium text-brand-dark leading-snug whitespace-pre-wrap">
                                                    {generatedText}
                                                </p>
                                                <div className="flex items-center justify-between px-4 pb-3">
                                                    <button
                                                        onClick={startEditing}
                                                        className="flex items-center gap-1.5 text-sm font-medium text-brand-red hover:opacity-80 transition"
                                                    >
                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                                                             stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                                             strokeLinejoin="round">
                                                            <path d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
                                                        </svg>
                                                        Править текст
                                                    </button>
                                                    <span className="text-sm text-brand-dark/50 tracking-[-0.14px]">
                                                        {generatedText.length}/{MAX_TEXT_LENGTH}
                                                    </span>
                                                </div>
                                            </>
                                        )}

                                        {/* Приклеенная плашка превью */}
                                        <div className="bg-brand-red/5 px-4 py-3">
                                            {isEditingText ? (
                                                <div className="flex items-center gap-3">
                                                    <PreviewPlayCircle/>
                                                    <p className="text-sm font-medium text-brand-red/90 leading-snug">
                                                        Вы правите текст. Сохраните — и озвучим заново.
                                                    </p>
                                                </div>
                                            ) : previewLoading ? (
                                                <div className="flex items-center gap-3">
                                                    <PreviewPlayCircle loading/>
                                                    <p className="text-sm font-medium text-brand-dark/60 leading-snug">
                                                        Озвучиваем…
                                                    </p>
                                                </div>
                                            ) : previewReady ? (
                                                // Кнопка в кнопку вкладывать нельзя — плей и переозвучка
                                                // лежат рядом в общей строке.
                                                <div className="flex items-center gap-3">
                                                    <button
                                                        onClick={handleTogglePlay}
                                                        className="flex-1 min-w-0 flex items-center gap-3 text-left"
                                                    >
                                                        <PreviewPlayCircle active playing={previewPlaying}/>
                                                        <span className="text-sm font-medium text-brand-dark">
                                                            {previewPlaying ? 'Остановить' : 'Послушать превью'}
                                                        </span>
                                                    </button>
                                                    <button
                                                        onClick={handleRegenerateFromText}
                                                        aria-label="Озвучить заново"
                                                        title="Озвучить заново"
                                                        className="shrink-0 w-9 h-9 rounded-full text-brand-dark/40 hover:text-brand-red hover:bg-brand-red/10 flex items-center justify-center transition"
                                                    >
                                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                                                             stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                                             strokeLinejoin="round">
                                                            <path d="M23 4v6h-6M1 20v-6h6"/>
                                                            <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
                                                        </svg>
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="flex items-center gap-3 mb-3">
                                                        <PreviewPlayCircle/>
                                                        <p className="text-sm font-medium text-brand-red/90 leading-snug">
                                                            {preview ? 'Старое превью — текст или звезда изменились.' : 'Текст ещё не озвучен.'}
                                                        </p>
                                                    </div>
                                                    <button
                                                        onClick={handleRegenerateFromText}
                                                        className="w-full py-3 rounded-xl bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-sm font-medium tracking-[-0.14px] hover:opacity-95 transition flex items-center justify-center gap-2"
                                                    >
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                                             stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                                             strokeLinejoin="round">
                                                            <path d="M23 4v6h-6M1 20v-6h6"/>
                                                            <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
                                                        </svg>
                                                        {preview ? 'Озвучить заново' : 'Озвучить текст'}
                                                    </button>
                                                </>
                                            )}
                                            {previewError && (
                                                <p className="text-xs text-brand-red mt-2">{previewError}</p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <button
                                onClick={handleSubmit}
                                disabled={!previewReady || isEditingText || isSubmitting}
                                className="w-full py-4 rounded-2xl bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-sm font-medium tracking-[-0.14px] hover:opacity-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSubmitting
                                    ? 'Запускаем генерацию…'
                                    : credits > 0
                                        ? `Сгенерить видео (${credits} осталось)`
                                        : 'Сгенерить видео'
                                }
                            </button>
                            {!previewReady && !isEditingText && (
                                <p className="text-xs text-brand-dark/50 mt-2 text-center">
                                    Сначала сгенерируйте превью — этим голосом будет озвучено видео
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
